import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { Alert, Button, IconButton } from '@mui/material'
import { CloseRounded } from '@mui/icons-material'
import { useSmartTranslation } from '../generic/hooks/useSmartTranslation'

const FixedBottomDiv = (props) => (
  <div
    sx={{
      position: 'fixed',
      bottom: theme => theme.spacing(2),
      right: theme => theme.spacing(35)
    }}
  />
)

// `action` (optional, e.g. Retry): MUI drops the ✕ when an Alert has an action, so both are drawn here.
const AlertElement = ({ showAlert, handleCloseAlert, alertTitle, alertMessage, mode, action, sx }) => {
  const handleAction = action?.onClick
  return (showAlert
    ? (
      <Alert
        variant='outlined' severity={mode} onClose={handleCloseAlert} sx={{ backgroundColor: theme => theme.palette.background.color_212121, width: '100%', ...sx }}
        action={action
          ? (
            <>
              <Button color='inherit' size='small' onClick={handleAction}>{action.label}</Button>
              <IconButton color='inherit' size='small' aria-label='Close' onClick={handleCloseAlert}>
                <CloseRounded fontSize='small' />
              </IconButton>
            </>
            )
          : undefined}
      >
        <strong>{alertTitle}</strong> {alertMessage}
      </Alert>
      )
    : null
  )
}

// `sx` (optional): extra style of the notice on this screen, e.g. room below it; without it, as always.
const AlertUI = ({ sx } = {}) => {
  const [showAlert, setShowAlert] = useState(false)
  const [mode, setMode] = useState('error')
  const [alertTitle, setAlertTitle] = useState(undefined)
  const [alertMessage, setAlertMessage] = useState(undefined)
  const [fixedBottom, setFixedBottom] = useState(false)
  const [duration, setDuration] = useState(5000)
  const [action, setAction] = useState(undefined)
  const [onCloseCallback, setOnCloseCallback] = useState(undefined)
  const location = useLocation()
  const { t_ } = useSmartTranslation()

  const defaultValues = useMemo(() => {
    return {
      error: {
        title: t_('¡Ups, something has gone wrong!'),
        message: ''
      },
      info: {
        title: t_('There have been no modifications.'),
        message: ''
      },
      success: {
        title: t_('Operation successful.'),
        message: ' '
      },
      warning: {
        title: t_('Warning!'),
        message: ''
      }
    }
  }, [t_])

  useEffect(() => {
    let timer
    if (showAlert && duration !== null && duration > 0) {
      timer = setTimeout(function () {
        setShowAlert(false)
      }, duration)
    }
    return () => {
      if (timer) clearTimeout(timer)
    }
  }, [showAlert, duration])

  useEffect(() => {
    setShowAlert(false)
  }, [location])

  const handleCloseAlert = useCallback(() => {
    setShowAlert(false)
    if (onCloseCallback) onCloseCallback()
  }, [onCloseCallback])

  const getTitle = useCallback((title, severity) => {
    if (title === undefined) {
      return defaultValues[severity].title
    }
    return title
  }, [defaultValues])

  const getMessage = useCallback((message, severity) => {
    if (message === undefined || message === '') {
      return defaultValues[severity].message
    }
    return message
  }, [defaultValues])

  const show = useCallback((
    { severity = 'success', title = undefined, message = undefined, fixedBottom = undefined, duration: customDuration = undefined, action = undefined, onClose = undefined }
  ) => {
    setMode(severity)
    setAction(action)
    setOnCloseCallback(() => onClose)
    setAlertTitle(getTitle(title, severity))
    setAlertMessage(getMessage(message, severity))
    setShowAlert(true)
    setFixedBottom(fixedBottom)

    if (customDuration !== undefined) {
      setDuration(customDuration)
    } else {
      setDuration((severity === 'info' || severity === 'success') ? 5000 : null)
    }
  }, [getMessage, getTitle])

  const dismiss = useCallback(() => {
    setShowAlert(false)
  }, [])

  return useMemo(() => [
    fixedBottom
      ? <FixedBottomDiv><AlertElement showAlert={showAlert} handleCloseAlert={handleCloseAlert} alertTitle={alertTitle} alertMessage={alertMessage} mode={mode} action={action} sx={sx} /></FixedBottomDiv>
      : <AlertElement showAlert={showAlert} handleCloseAlert={handleCloseAlert} alertTitle={alertTitle} alertMessage={alertMessage} mode={mode} action={action} sx={sx} />,
    show,
    dismiss
  ], [fixedBottom, show, dismiss, alertMessage, handleCloseAlert, alertTitle, mode, showAlert, action, sx])
}

export default AlertUI
